import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  forwardRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { GooglePlacePrediction, UtilsService } from '../utils.service';

/**
 * The structured result emitted when a user selects an address from the lookup.
 */
export interface ResolvedAddress {
  formattedAddress: string;
  address: string;
  suburb: string;
  city: string;
  postalCode: string;
  country: string;
  location: any | null;
}

/**
 * Reusable Google Places address lookup field.
 *
 * Acts as a form control (ControlValueAccessor + Validator) so it can be bound with
 * [(ngModel)] and `required`, while also emitting a structured ResolvedAddress on selection
 * so hosts can pre-populate separate address fields.
 */
@Component({
  selector: 'app-address-lookup',
  templateUrl: './address-lookup.component.html',
  styleUrls: ['./address-lookup.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressLookupComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressLookupComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddressLookupComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() label: string = 'Address Lookup';
  @Input() placeholder: string = 'Search for an address';
  @Input() hint: string = '';
  @Input() required: boolean = false;

  /**
   * Optional provider that returns a google.maps.LatLngBounds (or bounds literal) to bias
   * predictions. Supplied as a function so hosts can return live values (e.g. the current map
   * viewport) at query time.
   */
  @Input() locationBiasProvider: (() => any) | null = null;

  /**
   * Emitted when the user selects an address. Hosts can use this to pre-populate fields.
   */
  @Output() addressSelected: EventEmitter<ResolvedAddress> = new EventEmitter<
    ResolvedAddress
  >();

  value: string = '';
  predictions: GooglePlacePrediction[] = [];
  disabled: boolean = false;
  touched: boolean = false;

  private input$: Subject<string> = new Subject<string>();
  private sessionToken: any = null;
  private destroy$: Subject<void> = new Subject<void>();

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(public utils: UtilsService) {}

  ngOnInit(): void {
    this.input$
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (query: string): void => {
          if (!query || query.trim().length < 3) {
            this.predictions = [];
            return;
          }

          // Start a session on first keystroke of a new search.
          if (!this.sessionToken) {
            this.utils
              .createPlacesSessionToken()
              .then(
                (token: any): void => {
                  this.sessionToken = token;
                }
              )
              .catch(
                (): void => {
                  /* token is optional; predictions still work without it */
                }
              );
          }

          const bias: any = this.locationBiasProvider
            ? this.locationBiasProvider()
            : undefined;
          this.utils
            .getPlacePredictions(query, this.sessionToken, bias)
            .then(
              (predictions: GooglePlacePrediction[]): void => {
                this.predictions = predictions;
              }
            )
            .catch(
              (): void => {
                this.predictions = [];
              }
            );
        }
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Keep the model in sync as the user types and push the query into the debounced stream.
   */
  onInputChange(value: string): void {
    this.value = value || '';
    this.onChange(this.value);
    this.input$.next(this.value);
  }

  /**
   * Mark the control as touched when the field loses focus.
   */
  onBlur(): void {
    this.markTouched();
  }

  /**
   * Resolve the selected prediction's details, update the value and emit the structured address.
   */
  onSelect(prediction: GooglePlacePrediction): void {
    this.utils
      .getPlaceDetails(prediction.placePrediction)
      .then(
        (details): void => {
          const resolved: ResolvedAddress = this.mapToResolvedAddress(
            details.addressComponents || [],
            details.formattedAddress,
            details.location
          );

          this.value = details.formattedAddress || this.value;
          this.onChange(this.value);
          this.predictions = [];
          // The details fetch ends the billing session; start a fresh one for the next search.
          this.sessionToken = null;

          this.addressSelected.emit(resolved);
        }
      )
      .catch(
        (): void => {
          /* ignore: user can still enter the address manually */
        }
      );
  }

  /**
   * Map Google address components into a structured ResolvedAddress.
   */
  private mapToResolvedAddress(
    components: any[],
    formattedAddress: string,
    location: any
  ): ResolvedAddress {
    const get = (type: string): string => {
      const component = components.find((c: any) =>
        (c.types || []).includes(type)
      );
      return component ? component.longText || component.shortText || '' : '';
    };

    const street = `${get('street_number')} ${get('route')}`.trim();

    return {
      formattedAddress: formattedAddress || '',
      address: street || formattedAddress || '',
      suburb:
        get('sublocality_level_1') ||
        get('sublocality') ||
        get('neighborhood') ||
        '',
      city: get('locality') || get('postal_town') || '',
      postalCode: get('postal_code') || '',
      country: get('country') || '',
      location: location || null
    };
  }

  private markTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  // --- ControlValueAccessor ---

  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Validator ---

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.required && (!this.value || !this.value.trim())) {
      return { required: true };
    }
    return null;
  }
}
